import React from "react";
import PropTypes from "prop-types";
import { Box, FormHelperText } from "@mui/material";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { colors } from "../../../../theme";

/**
 * CommonEditor Component
 * Reusable rich-text WYSIWYG editor powered by react-draft-wysiwyg.
 * Adheres to the AutoBET design tokens, focus states, and styling standards.
 */
const CommonEditor = ({
  editorState,
  onEditorStateChange,
  onChange,
  placeholder = "Write content here...",
  error = false,
  helperText,
  minHeight = "200px",
  maxHeight = "320px",
  readOnly = false,
  toolbarHidden = false,
  toolbar,
  borderRadius = "14px",
  sx = {},
  editorSx = {},
  toolbarSx = {},
  ...props
}) => {
  const isError = Boolean(error);
  const errorMessage = typeof error === "string" ? error : helperText;

  const defaultToolbar = {
    options: [
      "inline",
      "blockType",
      "fontSize",
      "list",
      "textAlign",
      "link",
      "history",
    ],
    inline: {
      inDropdown: false,
      options: ["bold", "italic", "underline", "strikethrough"],
    },
    blockType: {
      inDropdown: true,
      options: ["Normal", "H1", "H2", "H3", "Blockquote"],
    },
    list: {
      inDropdown: false,
      options: ["unordered", "ordered"],
    },
  };

  const handleChange = (state) => {
    if (onEditorStateChange) {
      onEditorStateChange(state);
    } else if (onChange) {
      onChange(state);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          border: `1px solid ${
            isError
              ? colors.error?.main || "#ef4444"
              : colors.dashboard?.cardBorder || colors.slate[200]
          }`,
          borderRadius: borderRadius,
          overflow: "hidden",
          bgcolor: colors.background?.paper || "#ffffff",
          transition: "border-color 0.2s, box-shadow 0.2s",
          "&:focus-within": {
            borderColor: isError
              ? colors.error?.main || "#ef4444"
              : colors.primary?.main || "#2563eb",
            boxShadow: isError
              ? "0 0 0 3px rgba(239, 68, 68, 0.12)"
              : `0 0 0 3px ${colors.primary?.subtle || "rgba(37, 99, 235, 0.12)"}`,
          },
          "& .rdw-editor-toolbar": {
            borderTop: "none",
            borderLeft: "none",
            borderRight: "none",
            borderBottom: `1px solid ${colors.dashboard?.cardBorder || colors.slate[200]}`,
            bgcolor: colors.slate[50] || "#f8fafc",
            p: 1.25,
            mb: 0,
            borderRadius: `${borderRadius} ${borderRadius} 0 0`,
            ...toolbarSx,
          },
          "& .rdw-option-wrapper": {
            border: `1px solid ${colors.slate[200]}`,
            borderRadius: "6px",
            padding: "5px",
            minWidth: "26px",
            height: "26px",
            "&:hover": {
              boxShadow: "none",
              borderColor: colors.primary?.main || "#2563eb",
              bgcolor: colors.primary?.lighter || "#eff6ff",
            },
          },
          "& .rdw-option-active": {
            boxShadow: "none",
            borderColor: colors.primary?.main || "#2563eb",
            bgcolor: colors.primary?.lighter || "#eff6ff",
          },
          "& .rdw-dropdown-wrapper": {
            border: `1px solid ${colors.slate[200]}`,
            borderRadius: "6px",
            height: "28px",
          },
          "& .rdw-dropdown-optionwrapper": {
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            border: `1px solid ${colors.slate[200]}`,
          },
          "& .rdw-editor-main": {
            minHeight,
            maxHeight,
            overflowY: "auto",
            p: 2,
            fontSize: "0.92rem",
            color: colors.slate[800],
            lineHeight: 1.6,
            ...editorSx,
          },
          ...sx,
        }}
      >
        <Editor
          editorState={editorState}
          onEditorStateChange={handleChange}
          placeholder={placeholder}
          readOnly={readOnly}
          toolbarHidden={toolbarHidden || readOnly}
          toolbar={toolbar || defaultToolbar}
          {...props}
        />
      </Box>
      {isError && errorMessage && (
        <FormHelperText error sx={{ mt: 0.75, fontWeight: 500 }}>
          {errorMessage}
        </FormHelperText>
      )}
    </Box>
  );
};

CommonEditor.propTypes = {
  editorState: PropTypes.object,
  onEditorStateChange: PropTypes.func,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  helperText: PropTypes.node,
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  readOnly: PropTypes.bool,
  toolbarHidden: PropTypes.bool,
  toolbar: PropTypes.object,
  borderRadius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sx: PropTypes.object,
  editorSx: PropTypes.object,
  toolbarSx: PropTypes.object,
};

export default CommonEditor;
